import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { getCycleTiming } from '../cycleTiming';

export interface SummaryCycle {
  start_date: string;
  end_date?: string | null;
  first_peak_cd?: number | null;
  /** Partial cycle: logged before the first recorded cycle start, so its CD 1 is unknown. */
  start_inferred?: boolean;
}

export type CycleSummaryRow = [string, string, string, number | string, number | string];

/**
 * Rows for the PDF "Cycle Summary" table (newest first, at most 12), shared by the download
 * and email exports.
 *
 * Length is inclusive (start and end day both count), matching Cycle History and Insights.
 * Dates are parsed as local calendar days — `new Date("yyyy-MM-dd")` is UTC midnight and
 * prints as the previous day west of UTC.
 */
export const buildCycleSummaryRows = (
  cycles: SummaryCycle[],
  todayKey: string
): CycleSummaryRow[] =>
  cycles.slice(0, 12).map((cycle, idx) => {
    // Pre-logged (future-dated) cycles are exported as data, but marked as upcoming; the
    // cycle containing today is ongoing even if a pre-logged next start gave it an end date.
    const timing = getCycleTiming(cycle, todayKey);
    const start = parseISO(cycle.start_date);
    const end = cycle.end_date ? parseISO(cycle.end_date) : null;

    // A partial cycle's start was never recorded, so it has no length or CD-numbered Peak,
    // and it isn't counted when numbering the recorded cycles.
    if (cycle.start_inferred) {
      return [
        'Partial (start not recorded)',
        `Not recorded (first entry ${format(start, 'PP')})`,
        end ? format(end, 'PP') : 'Ongoing',
        '-',
        '-',
      ];
    }

    const length =
      end && timing !== 'current' ? differenceInCalendarDays(end, start) + 1 : 'Ongoing';
    const cycleNumber = cycles.slice(idx).filter((c) => !c.start_inferred).length;

    return [
      timing === 'upcoming' ? `Cycle ${cycleNumber} (upcoming)` : `Cycle ${cycleNumber}`,
      format(start, 'PP'),
      end ? format(end, 'PP') : 'Ongoing',
      length,
      cycle.first_peak_cd || '-',
    ];
  });
